import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import MainLayout from './components/Layout/MainLayout';
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import axios from 'axios';

// Admin Pages
import AdminDashboard from './pages/Admin/Dashboard';
import RosterPage from './pages/Admin/RosterPage';
import ParticipantsPage from './pages/Admin/ParticipantsPage';
import ParticipantProfilePage from './pages/Admin/ParticipantProfilePage';
import IncidentsPage from './pages/Admin/IncidentsPage';
import DiaryPage from './pages/Admin/DiaryPage';
import HousesPage from './pages/Admin/HousesPage';
import UsersPage from './pages/Admin/UsersPage';
import WorkerProfilePage from './pages/Admin/WorkerProfilePage';
import CompliancePage from './pages/Admin/CompliancePage';
import AuditLogsPage from './pages/Admin/AuditLogsPage';
import ArchivedPage from './pages/Admin/ArchivedPage';

// Manager Pages
import MyTeamPage from './pages/Manager/MyTeamPage';

// Worker Pages
import WorkerDashboard from './pages/Worker/Dashboard';
import WorkerIncidentsPage from './pages/Worker/IncidentsPage';
import WorkerOwnProfilePage from './pages/Worker/ProfilePage';
import TeamShiftNotesPage from './pages/Worker/TeamShiftNotesPage';
import ClientProfilesPage from './pages/Worker/ClientProfilesPage';

// Family Pages
import FamilyDashboard from './pages/Family/Dashboard';

// Shared Pages
import LeavePage from './pages/shared/LeavePage';
import ShiftNotesPage from './pages/shared/ShiftNotesPage';
import CommunicationsPage from './pages/shared/CommunicationsPage';
import SettingsPage from './pages/shared/SettingsPage';
import NotificationsPage from './pages/shared/NotificationsPage';
import TimesheetReportPage from './pages/shared/TimesheetReportPage';

import './index.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    const roleRoutes = {
      admin: '/admin',
      house_manager: '/manager',
      worker: '/worker',
      family: '/family'
    };
    return <Navigate to={roleRoutes[user?.role] || '/'} replace />;
  }

  return children;
};

// Root redirect based on role
const RootRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const roleRoutes = {
    admin: '/admin',
    house_manager: '/manager',
    worker: '/worker',
    family: '/family'
  };

  return <Navigate to={roleRoutes[user?.role] || '/login'} replace />;
};

// Setup Check Component - redirects to setup if no admin exists
const SetupCheck = ({ children }) => {
  const [setupRequired, setSetupRequired] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/setup/status`);
        setSetupRequired(response.data.setup_required);
      } catch (err) {
        console.error('Failed to check setup status:', err);
        setSetupRequired(false); // Assume setup done if check fails
      } finally {
        setLoading(false);
      }
    };
    checkSetup();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (setupRequired) {
    return <Navigate to="/setup" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Setup Route - for initial admin creation */}
            <Route path="/setup" element={<SetupPage />} />
            
            {/* Public Routes */}
            <Route path="/login" element={<SetupCheck><LoginPage /></SetupCheck>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Root Redirect */}
            <Route path="/" element={<SetupCheck><RootRedirect /></SetupCheck>} />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="roster" element={<RosterPage />} />
              <Route path="participants" element={<ParticipantsPage />} />
              <Route path="participants/:id" element={<ParticipantProfilePage />} />
              <Route path="houses" element={<HousesPage />} />
              <Route path="notes" element={<ShiftNotesPage />} />
              <Route path="timesheets" element={<TimesheetReportPage />} />
              <Route path="incidents" element={<IncidentsPage />} />
              <Route path="diary" element={<DiaryPage />} />
              <Route path="leave" element={<LeavePage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<WorkerProfilePage />} />
              <Route path="compliance" element={<CompliancePage />} />
              <Route path="archived" element={<ArchivedPage />} />
              <Route path="audit" element={<AuditLogsPage />} />
              <Route path="communications" element={<CommunicationsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* House Manager Routes */}
            <Route
              path="/manager"
              element={
                <ProtectedRoute allowedRoles={['house_manager']}>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="roster" element={<RosterPage />} />
              <Route path="participants" element={<ParticipantsPage />} />
              <Route path="participants/:id" element={<ParticipantProfilePage />} />
              <Route path="notes" element={<ShiftNotesPage />} />
              <Route path="timesheets" element={<TimesheetReportPage />} />
              <Route path="incidents" element={<IncidentsPage />} />
              <Route path="diary" element={<DiaryPage />} />
              <Route path="leave" element={<LeavePage />} />
              <Route path="team" element={<MyTeamPage />} />
              <Route path="archived" element={<ArchivedPage />} />
              <Route path="communications" element={<CommunicationsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Worker Routes */}
            <Route
              path="/worker"
              element={
                <ProtectedRoute allowedRoles={['worker', 'house_manager']}>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<WorkerDashboard />} />
              <Route path="roster" element={<RosterPage />} />
              <Route path="team" element={<RosterPage />} />
              <Route path="notes" element={<ShiftNotesPage />} />
              <Route path="team-notes" element={<TeamShiftNotesPage />} />
              <Route path="clients" element={<ClientProfilesPage />} />
              <Route path="communications" element={<CommunicationsPage />} />
              <Route path="incidents" element={<WorkerIncidentsPage />} />
              <Route path="diary" element={<DiaryPage />} />
              <Route path="leave" element={<LeavePage isWorker />} />
              <Route path="profile" element={<WorkerOwnProfilePage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Family Routes */}
            <Route
              path="/family"
              element={
                <ProtectedRoute allowedRoles={['family']}>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<FamilyDashboard />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="diary" element={<DiaryPage />} />
              <Route path="incidents" element={<IncidentsPage />} />
            </Route>

            {/* Settings (all roles) */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<div className="p-8"><h1 className="text-2xl font-bold">Settings</h1><p className="text-muted-foreground mt-2">Coming soon...</p></div>} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
